// ─── Los módulos de una plantilla de syllabus: lo que no es pantalla (S1·S2) ─
// Una sesión guarda `dayIndex`: la POSICIÓN de su día en `module.dayOffsets`.
// No hay un id de día, así que borrar un día cambia la posición de los que
// siguen. Antes de S1 el editor borraba el día y no tocaba las sesiones: las del
// día borrado pasaban al día siguiente y las de los días posteriores se corrían
// una posición o quedaban fuera de rango. Caso real en SEBEX: Módulo I con
// viernes y sábado; al borrar el viernes, las sesiones 1–3 aparecieron en el
// sábado y las 4–10 quedaron sin día.
//
// Un id estable de día no hace falta: los únicos que guardan una posición de día
// son las sesiones (que se reindexan acá) y los ajustes de fecha del asistente
// de generación (`módulo::dayIndex`), que viven solo mientras dura el asistente.

/** Una sesión con `dayIndex` fuera de los días del módulo no tiene día: el
 *  editor la muestra aparte («Sesiones sin día»). `-1` es la marca que deja
 *  «Quitar del día». */
export function hasNoDay(dayIndex: number, dayCount: number): boolean {
  return dayIndex < 0 || dayIndex >= dayCount;
}

export function sessionsOnDay(module: { sessions: { dayIndex: number }[] }, day: number): number {
  return module.sessions.filter((s) => s.dayIndex === day).length;
}

/** Qué pasa con las sesiones del día que se borra. */
export type DeleteDayPlan = { kind: 'move'; to: number } | { kind: 'drop' };

/**
 * Borra el día `day` de un módulo y deja cada sesión en el día en que estaba:
 *  - las del día borrado se mueven a `plan.to` o se borran (`plan.drop`);
 *  - las de días posteriores bajan su `dayIndex` en 1;
 *  - las que no tenían día siguen sin día.
 * Las sesiones movidas quedan en orden cronológico respecto de las del día
 * destino: delante si el destino era posterior, detrás si era anterior.
 * Un día o un destino inválido devuelve el módulo sin cambios.
 */
export function deleteModuleDay<
  S extends { dayIndex: number },
  M extends { dayOffsets: readonly unknown[]; sessions: S[] },
>(module: M, day: number, plan: DeleteDayPlan): M {
  const count = module.dayOffsets.length;
  if (!Number.isInteger(day) || day < 0 || day >= count) return module;
  if (plan.kind === 'move' && (plan.to === day || hasNoDay(plan.to, count))) return module;

  const shift = (d: number): number => (hasNoDay(d, count) || d < day ? d : d - 1);
  const dayOffsets = module.dayOffsets.filter((_, i) => i !== day);
  const rest = module.sessions
    .filter((s) => s.dayIndex !== day)
    .map((s) => (shift(s.dayIndex) === s.dayIndex ? s : { ...s, dayIndex: shift(s.dayIndex) }));

  if (plan.kind === 'drop') return { ...module, dayOffsets, sessions: rest };

  const to = shift(plan.to);
  const moved = module.sessions
    .filter((s) => s.dayIndex === day)
    .map((s) => ({ ...s, dayIndex: to }));
  const onTarget = rest.flatMap((s, i) => (s.dayIndex === to ? [i] : []));
  let at = rest.length;
  if (onTarget.length > 0) at = plan.to > day ? onTarget[0]! : onTarget[onTarget.length - 1]! + 1;
  const sessions = [...rest.slice(0, at), ...moved, ...rest.slice(at)];
  return { ...module, dayOffsets, sessions };
}

// ─── Copiar los módulos de otra plantilla ───────────────────────────────────
// «Todo» trae el cronograma tal cual, con temas y textos clave. «Solo la
// estructura» trae días, bloques y duraciones con temas genéricos y SIN textos
// clave ni eje temático: antes la copia era siempre completa, y los versículos
// de otra materia —que el editor no mostraba— terminaban en el PDF.

export type CopyModulesMode = 'structure' | 'all';

interface CopyableSession {
  id: string;
  dayIndex: number;
  topic: string;
  durationMinutes?: number;
  scriptureRefs?: unknown[];
}

export function copyModules<
  S extends CopyableSession,
  M extends { axisTopic: string; dayOffsets: readonly unknown[]; sessions: S[] },
>(modules: M[], mode: CopyModulesMode, genericTopic: (n: number) => string): M[] {
  if (mode === 'all') return modules;
  return modules.map((m) => ({
    ...m,
    axisTopic: '',
    sessions: m.sessions
      .filter((s) => !hasNoDay(s.dayIndex, m.dayOffsets.length))
      .map((s, k) => {
        const { scriptureRefs: _refs, ...rest } = s;
        return { ...rest, topic: genericTopic(k + 1) } as S;
      }),
  }));
}
